/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { DEFAULT_LANG, LANG_STORAGE_KEY, type Lang } from '@/lib/i18n';

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function readStoredLang(): Lang {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
  return stored === 'en' || stored === 'fr' ? stored : DEFAULT_LANG;
}

interface ProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: ProviderProps) {
  const [lang, setLangState] = useState<Lang>(() => readStoredLang());

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ lang: Lang }>).detail;
      if (detail && (detail.lang === 'fr' || detail.lang === 'en')) {
        setLangState(detail.lang);
      }
    };
    window.addEventListener('language-change', handler);
    return () => window.removeEventListener('language-change', handler);
  }, []);

  const setLang = useCallback((value: Lang) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANG_STORAGE_KEY, value);
      window.dispatchEvent(new CustomEvent('language-change', { detail: { lang: value } }));
    }
    setLangState(value);
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLang must be used inside <LanguageProvider>.');
  }
  return ctx;
}
